import React, { useCallback, useEffect, useMemo, useState } from "react"

import { Badge, Dropdown, Space, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { FixedSizeList as List } from "react-window"

import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import Images from "../../Images"
import utils, {
  paymentProcessorMap,
  serviceMap,
  statusMap,
  statusTextMap,
  tagColorMap,
  tagMap
} from "../../Utils/utils"
import InputComponent from "../Input"
import { INCOGNITO_MASK } from "../Private"

import "./DropdownBlock.less"

const { Text } = Typography
const DropdownBlockCheckbox = ({
  label,
  iconType,
  id,
  withSearch,
  allBtnText,
  dropdownFilters,
  setDropdownFilters,
  data,
  disabled,
  filterKey,
  newTagStyle,
  type,
  classNameWrapper,
  newMarkStyle,
  withNoOption,
  noOptionLabel,
  allOptionLast,
  sortDisabled,
  onOpenChange,
  noStatusStyles,
  activeDropdownId,
  showSelectedCount
}) => {
  const { t } = useTranslation()
  //state toggle show/hide dropdown
  const [visible, setVisible] = useState(false)
  //check all checkbox state
  const [checkAll, setCheckAll] = useState(false)
  //search query in dropdown
  const [search, setSearch] = useState("")

  const isDisabledTransition = useSelector((state) => state.nav.disabledTransition)
  const incognito = useIncognito()
  const isMaskedFilter = id === "descriptors" && incognito

  const defaultFilterKey =
    filterKey ||
    (typeof data?.[0] === "object" &&
    data?.[0] &&
    Object.prototype.hasOwnProperty.call(data[0], "id")
      ? "id"
      : null)

  const getOptionValue = useCallback(
    (option) => (typeof option === "object" ? option?.[defaultFilterKey || "name"] : option),
    [defaultFilterKey]
  )

  //check all if no initial value and nothing selected on close
  useEffect(() => {
    if (!visible && dropdownFilters !== false && (!dropdownFilters || !dropdownFilters.length)) {
      setDropdownFilters({ [id]: "all" })
      setCheckAll(true)
    }
  }, [visible, dropdownFilters, id, setDropdownFilters])

  //set initial value on load
  useEffect(() => {
    if (dropdownFilters === "all") {
      setCheckAll(true)
    } else if (!dropdownFilters) {
      setCheckAll(false)
    } else if (dropdownFilters?.length) {
      setCheckAll(dropdownFilters?.length === data?.length)
    }
  }, [dropdownFilters, data])

  const checkElement = (el, value) => {
    if (!value) return true
    const valueLowerCase = value?.toLowerCase()
    if (typeof el === "object") {
      return [el?.name, el?.value, el?.code].some((item) =>
        item?.toLowerCase()?.includes(valueLowerCase)
      )
    } else {
      return el?.toLowerCase()?.includes(valueLowerCase)
    }
  }

  const filteredData = useMemo(() => {
    const newSortedData = [...(data ?? [])].sort((a, b) => {
      const aValue = getOptionValue(a)
      const bValue = getOptionValue(b)
      const aIsEmpty = aValue === ""
      const bIsEmpty = bValue === ""

      if (aIsEmpty && !bIsEmpty) return -1
      if (!aIsEmpty && bIsEmpty) return 1

      const a_name = typeof a === "string" ? a : a?.name
      const b_name = typeof b === "string" ? b : b?.name
      return a_name?.localeCompare(b_name)
    })
    const sortedData = (sortDisabled ? (data ?? []) : newSortedData).filter((el) => !el?.hidden)
    const hasEmptyOption = sortedData.some((el) => getOptionValue(el) === "")

    // Build base options with "All" button and optionally "No option"
    const baseOptions = [{ id: "all", name: allBtnText }]
    if (withNoOption && !hasEmptyOption) {
      baseOptions.push({ id: "no_value", name: noOptionLabel, isNoValue: true })
    }

    if (!search) {
      if (data) {
        return allOptionLast ? [...sortedData, ...baseOptions] : [...baseOptions, ...sortedData]
      } else {
        return baseOptions
      }
    }
    return sortedData?.filter((el) => checkElement(el, search))
  }, [
    data,
    search,
    allBtnText,
    withNoOption,
    noOptionLabel,
    getOptionValue,
    sortDisabled,
    allOptionLast
  ])

  const notEmpty = filteredData.length > 0

  useEffect(() => {
    if (activeDropdownId === undefined || activeDropdownId === id) return

    setVisible(false)
  }, [activeDropdownId, id])

  const handleVisibleChange = (value) => {
    setVisible(value)
    setSearch("")
    onOpenChange?.(value)
  }

  //checkbox click handle
  const clickHandler = useCallback(
    (val) => {
      const isNoValue = val?.isNoValue
      let selectValue = isNoValue ? "no_value" : getOptionValue(val)

      if (isNoValue) {
        // When "No descriptor" is selected, send special signal to backend
        setDropdownFilters({ [id]: false })
        setCheckAll(false)
      } else if (
        Array.isArray(dropdownFilters) &&
        dropdownFilters?.some((el) => el === selectValue)
      ) {
        setDropdownFilters({ [id]: [...dropdownFilters?.filter((el) => el !== selectValue)] })
        setCheckAll(false)
      } else {
        if (dropdownFilters === "all" || dropdownFilters === null || dropdownFilters === false) {
          setDropdownFilters({ [id]: [selectValue] })
        } else {
          if (data?.length === dropdownFilters?.length + 1) {
            setCheckAll(true)
            setDropdownFilters({ [id]: "all" })
          } else {
            setDropdownFilters({ [id]: [...dropdownFilters, selectValue] })
          }
        }
      }
    },
    [dropdownFilters, data, id, setDropdownFilters, getOptionValue]
  )

  //main checkbox handler
  const allClickHandler = () => {
    const checked = !checkAll
    if (checked) setDropdownFilters({ [id]: "all" })
    else setDropdownFilters({ [id]: null })
    setCheckAll(checked)
  }

  const handleChangeSearch = (e) => {
    const value = e.target.value
    setSearch(value)
  }

  //text to display after closed dropdown
  const getSelectValue = useCallback(() => {
    if (dropdownFilters === false) return `${label}: ${noOptionLabel}`

    if (!dropdownFilters?.length || dropdownFilters === "all") return `${label} ${t(allBtnText)}`

    if (dropdownFilters.length === 1 && dropdownFilters.length !== data?.length) {
      const firstValue = dropdownFilters[0]
      if (firstValue === "") {
        return `${label}: ${noOptionLabel}`
      }
      const displayValue = defaultFilterKey
        ? data.find((el) => el?.[defaultFilterKey] === firstValue)?.name
        : firstValue
      if (isMaskedFilter) return `${label}: ${INCOGNITO_MASK}`
      const enrollmentLabel = id === "enrollments" && serviceMap[displayValue?.toLowerCase()]
      return `${label}: ${statusTextMap[displayValue] || enrollmentLabel || utils.toUpperLatter(displayValue)}`
    }

    if (dropdownFilters.length > 1) {
      return showSelectedCount
        ? `${label}: ${dropdownFilters.length}`
        : `${label}: ${t("Mixed Values")}`
    }

    return label
  }, [
    dropdownFilters,
    data,
    label,
    t,
    defaultFilterKey,
    allBtnText,
    noOptionLabel,
    showSelectedCount,
    isMaskedFilter
  ])

  const renderListItem = ({ index, style }) => {
    const el = filteredData[index]
    const optionValue = getOptionValue(el)
    let isActive =
      (Array.isArray(dropdownFilters) && dropdownFilters?.some((elem) => optionValue === elem)) ||
      (el?.isNoValue && dropdownFilters === false) ||
      checkAll
    let text =
      el?.isNoValue || optionValue === ""
        ? noOptionLabel
        : isMaskedFilter
          ? INCOGNITO_MASK
          : (id === "enrollments" && serviceMap[(el?.name || el)?.toLowerCase?.()]) ||
            paymentProcessorMap[el?.name || el] ||
            utils.toUpperLatter(el?.name || el)

    if (el.id === "all") {
      return (
        <div
          className={cls("dropdown-database-list-item", {
            "dropdown-database-list-item--active": checkAll
          })}
          onClick={allClickHandler}
          style={style}
        >
          <div
            className={cls("dropdown-database-list-item-checkbox", {
              "dropdown-database-list-item-checkbox--active": checkAll
            })}
          />
          {t(allBtnText)}
        </div>
      )
    }

    return (
      <div
        style={style}
        key={typeof el === "object" ? `${el?.id ?? el?.name ?? index}` : `${el}`}
        onClick={() => clickHandler(el)}
        className={cls("dropdown-database-list-item", {
          // 'dropdown-database-list-item-hidden':  el?.hidden || !(checkElement(el, search)),
          "dropdown-database-list-item-hidden": !checkElement(el, search),
          "dropdown-database-list-item--active": isActive
        })}
      >
        {newMarkStyle ? null : (
          <div
            className={cls("dropdown-database-list-item-checkbox", {
              "dropdown-database-list-item-checkbox--active": isActive
            })}
          />
        )}

        {(el?.flag ||
          (["payment_processor", "connected_payment_processors"]?.includes(id) &&
            el !== "-" &&
            !el?.isNoValue)) && (
          <img
            src={el?.flag || Images[`integration_${el}`]}
            onError={utils.addDefaultSrc}
            alt={el}
            width={20}
            height={20}
          />
        )}

        {id?.includes("status") && !noStatusStyles ? (
          <Tag
            color={(newTagStyle && statusMap[el]) || tagMap[el?.toLowerCase()] || "default"}
            key={el}
            style={{ margin: "-2px 0" }}
            className={`simple ${newTagStyle} ${newTagStyle && statusMap[el]}`}
          >
            <Space>
              {!newTagStyle ? (
                <Badge
                  status={tagMap[el?.toLowerCase() || "active"] || "default"}
                  color={tagColorMap[el]}
                />
              ) : null}
              <Text>{statusTextMap[el] || el}</Text>
            </Space>
          </Tag>
        ) : (
          <Text className="_value" ellipsis={{ tooltip: text }}>
            {text}
          </Text>
        )}

        {newMarkStyle && isActive ? (
          <Icon type="check_markblue" style={{ fontSize: 16, margin: "0 auto" }} />
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={cls("select-block", classNameWrapper, {
        "disabled-transition": isDisabledTransition
      })}
    >
      <Dropdown
        overlayClassName={"dropdown-database-wrapper"}
        getPopupContainer={() => document.getElementById("main-layout")}
        placement="bottomLeft"
        onOpenChange={handleVisibleChange}
        overlayStyle={{ width: 268 }}
        destroyPopupOnHide={true}
        dropdownRender={() => (
          <div className="dropdown-database-menu" style={{ maxHeight: 366 }}>
            {withSearch && (
              <div className="dropdown-search-wrapper">
                <InputComponent
                  className="dropdown-search"
                  prefix={<Icon type="search" role="icon" />}
                  placeholder={`${t("Search")}...`}
                  value={search}
                  onChange={handleChangeSearch}
                />
              </div>
            )}

            <div
              className="dropdown-database-list"
              //style={{maxHeight: 'calc(366px - 80px)'}}
            >
              {notEmpty ? (
                <List
                  height={filteredData.length <= 5 ? filteredData.length * 42 : 286} // Adjust height as needed
                  itemCount={filteredData.length}
                  itemSize={42} // Adjust item height as needed
                  width={"100%"}
                >
                  {renderListItem}
                </List>
              ) : (
                <div className="dropdown-database-list-item" style={{ justifyContent: "center" }}>
                  <Icon type="empty_warning" style={{ fontSize: 24, color: "#707ba0" }} />
                  {search && <Text type="secondary">{t("No results found")}</Text>}
                </div>
              )}
            </div>
          </div>
        )}
        trigger={["click"]}
        open={visible}
        disabled={disabled}
      >
        <div
          className={cls(
            "dropdown-database",
            {
              "ant-dropdown-open": visible,
              "ant-dropdown-active": dropdownFilters !== "all" && dropdownFilters?.length,
              "dropdown-database__chart": type === "chart"
            },
            id
          )}
        >
          {iconType && <Icon type={iconType} />}
          <Text className="dropdown-database_value">{getSelectValue()}</Text>
          <Icon type="chevron_down_small" className="arrow-icon" />
        </div>
      </Dropdown>
    </div>
  )
}

export default React.memo(DropdownBlockCheckbox)
